import { Card, CardContent } from '@/components/ui/card';
import { Shield, CheckCircle, AlertTriangle } from 'lucide-react';

export default function IndependencePolicyPage() {
  return (
    <div data-testid="independence-policy-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Standards & Policies</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Independence & Conflict of Interest Policy</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            IMCAC is committed to maintaining complete independence and integrity in all examination and certification activities.
          </p>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container max-w-4xl">
          {/* Primary Statement */}
          <Card className="imcac-card border-l-4 border-l-[#C5A572] mb-8">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <Shield className="h-8 w-8 text-[#C5A572] flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-3">Statement of Independence</h2>
                  <p className="text-base text-slate-600 leading-relaxed">
                    IMCAC maintains complete independence between examination activities and educational providers. Training organisations have no influence over examination content, assessment decisions, scoring, certification outcomes, or recertification determinations.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Policy Details */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Scope of Independence</h3>
              <div className="space-y-3">
                {[
                  'Examination content development is conducted independently from any training provider or educational institution',
                  'Assessment criteria and pass/fail standards are set by the Standard Setting Committee without external influence',
                  'Examiners and assessors are selected based on professional qualifications and subject matter expertise, not institutional affiliation',
                  'Certification decisions are made solely by the Certification Committee based on examination performance',
                  'No training provider has representation on examination development or assessment panels',
                  'Recertification requirements are determined independently of training programme availability',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Conflict of Interest Management</h3>
              <div className="space-y-3">
                {[
                  'All committee members, examiners, and staff must declare any potential conflicts of interest',
                  'Individuals with financial or professional ties to training providers must recuse themselves from relevant decisions',
                  'Annual conflict of interest declarations are required from all governance committee members',
                  'A dedicated compliance process monitors and manages declared conflicts',
                  'Breaches of the conflict of interest policy are subject to investigation by the Ethics Committee',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Transparency Commitments</h3>
              <div className="space-y-3">
                {[
                  'Examination blueprints and assessment criteria are published and accessible to all candidates',
                  'Certification requirements are clearly documented and publicly available',
                  'Appeals and complaints processes operate independently from certification decisions',
                  'Regular governance reviews ensure continued compliance with independence standards',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Disclaimer */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-4xl">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 leading-relaxed">
                This policy is reviewed annually by the Board of Directors and the Ethics Committee. Any changes are communicated to all stakeholders. IMCAC reserves the right to amend this policy as necessary to maintain alignment with international certification body best practices and ISO 17024 principles.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
